import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import IconButton from '@material-ui/core/IconButton'
import InputAdornment from '@material-ui/core/InputAdornment'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import moment from 'moment'
import React from 'react'
import { useSelector } from 'react-redux'
import { IRootReduxState } from 'types/reducers'
import { useIntl } from 'react-intl'

type StyleProps = { isDarkMode: boolean }

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        margins: {
            marginTop: theme.spacing(0),
            marginBottom: theme.spacing(1),
            '& .MuiOutlinedInput-root': {
                backgroundColor: (props: StyleProps) => props.isDarkMode ? theme.palette.background.paper : 'transparent',
                color: (props: StyleProps) => props.isDarkMode ? theme.palette.text.primary : 'inherit',
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: (props: StyleProps) => props.isDarkMode ? theme.palette.divider : 'rgba(0, 0, 0, 0.23)',
            },
            '& .MuiInputLabel-outlined': {
                color: (props: StyleProps) => props.isDarkMode ? theme.palette.text.secondary : 'rgba(0, 0, 0, 0.6)',
            },
        },

        pickerDialog: {
            width: 360,
            maxWidth: '360px !important',
            borderRadius: 8,
            overflow: 'hidden',
        },

        pickerRoot: {
            background: (props: StyleProps) => props.isDarkMode ? '#2d3748' : '#FFFFFF',
            padding: '20px 22px 16px 22px',
        },

        headerRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
        },

        navBtn: {
            width: 42,
            height: 42,
            border: (props: StyleProps) => props.isDarkMode ? '1px solid #4a5568' : '1px solid #D8DCE3',
            borderRadius: 8,
            background: (props: StyleProps) => props.isDarkMode ? '#3a4556' : '#FFFFFF',
            padding: 0,
        },

        navArrow: {
            fontSize: 28,
            lineHeight: 1,
            color: (props: StyleProps) => props.isDarkMode ? '#a0aec0' : '#8A93A5',
        },

        monthTitle: {
            fontSize: 18,
            lineHeight: '24px',
            fontWeight: 700,
            color: (props: StyleProps) => props.isDarkMode ? '#e2e8f0' : '#2B2F38',
        },

        weekHeader: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            marginBottom: 6,
        },

        weekday: {
            textAlign: 'center',
            fontSize: 14,
            lineHeight: '20px',
            color: (props: StyleProps) => props.isDarkMode ? '#a0aec0' : '#7A8496',
            fontWeight: 500,
            paddingBottom: 4,
        },

        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            rowGap: 6,
        },

        dayCell: {
            width: 40,
            height: 40,
            border: 0,
            background: 'transparent',
            borderRadius: 8,
            fontSize: 14,
            lineHeight: '20px',
            color: (props: StyleProps) => props.isDarkMode ? '#cbd5e0' : '#2B2F38',
            cursor: 'pointer',
            justifySelf: 'center',
        },

        dayMuted: {
            color: (props: StyleProps) => props.isDarkMode ? '#718096' : '#8C95A6',
        },

        dayDisabled: {
            opacity: 0.3,
            cursor: 'not-allowed',
        },

        daySelected: {
            background: '#2563EB',
            color: '#FFFFFF',
            fontWeight: 600,
        },

        dayToday: {
            border: (props: StyleProps) => `1px solid ${props.isDarkMode ? '#63b3ed' : '#2563EB'}`,
            fontWeight: 700,
        },

        footerRow: {
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 12,
        },

        linkBtn: {
            border: 0,
            background: 'transparent',
            color: '#2563EB',
            fontSize: 14,
            lineHeight: '20px',
            cursor: 'pointer',
            padding: 0,
        },

        actions: {
            padding: '16px 20px',
            background: (props: StyleProps) => props.isDarkMode ? '#2d3748' : '#F9FAFB',
            borderTop: (props: StyleProps) => props.isDarkMode ? '1px solid #4a5568' : '1px solid #D9DDE5',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            '& button': {
                textTransform: 'none',
                fontWeight: 500,
                padding: '10px 28px',
                borderRadius: 8,
                fontSize: '14px',
                border: 'none',
                cursor: 'pointer',
            },
            '& button:first-of-type': {
                color: (props: StyleProps) => props.isDarkMode ? '#a0aec0' : '#6B7280',
                backgroundColor: (props: StyleProps) => props.isDarkMode ? '#3a4556' : '#F3F4F6',
                '&:hover': {
                    backgroundColor: (props: StyleProps) => props.isDarkMode ? '#4a5568' : '#E5E7EB',
                },
            },
            '& button:last-of-type': {
                backgroundColor: '#2563EB',
                color: '#FFFFFF',
                fontWeight: 600,
                '&:hover': {
                    backgroundColor: '#1D4ED8',
                },
            },
        },
    })
)

interface Props {
    name: string
    value?: string
    onChange: (dateStr: string) => void
    label?: React.ReactNode
    disabled?: boolean
    minDate?: string | Date | moment.Moment
    required?: boolean
    className?: string
    size?: 'small' | 'medium'
}

export const WaoDateOnly: React.FC<Props> = ({ name, value, onChange, label, disabled = false, minDate, className, size = 'medium' }) => {
    const minMoment = minDate ? moment(minDate) : null
    const isDarkMode = useSelector((state: IRootReduxState) => state.app.user.theme === 'dark')
    const classes = useStyles({ isDarkMode })
    const intl = useIntl()
    const momentLocale = intl.locale === 'zh' ? 'zh-cn' : intl.locale

    const toMoment = (v?: string) => {
        const m = v ? moment(v) : moment()
        return m.isValid() ? m : moment()
    }

    const [open, setOpen] = React.useState(false)
    const [displayMonth, setDisplayMonth] = React.useState(toMoment(value).startOf('month'))
    const [selected, setSelected] = React.useState<moment.Moment | null>(value ? toMoment(value) : null)
    const [preventReopen, setPreventReopen] = React.useState(false)

    React.useEffect(() => {
        if (value) {
            const m = moment(value)
            if (m.isValid()) {
                setSelected(m.clone())
                setDisplayMonth(m.clone().startOf('month'))
            }
        } else {
            setSelected(null)
        }
    }, [value])

    const weekDays = Array.from({ length: 7 }, (_, i) => moment().locale(momentLocale).day(i).format('dd'))
    const monthLabel = displayMonth.clone().locale(momentLocale).format('MMMM YYYY')

    const start = displayMonth.clone().startOf('week')
    const end = displayMonth.clone().endOf('month').endOf('week')
    const days: moment.Moment[] = []
    const cursor = start.clone()
    while (cursor.isSameOrBefore(end, 'day')) {
        days.push(cursor.clone())
        cursor.add(1, 'day')
    }

    const applyAndClose = () => {
        if (selected) {
            onChange(selected.format('YYYY-MM-DD'))
        }
        setPreventReopen(true)
        setOpen(false)
        setTimeout(() => setPreventReopen(false), 150)
    }

    const clearValue = () => {
        setSelected(null)
        onChange('')
        setPreventReopen(true)
        setOpen(false)
        setTimeout(() => setPreventReopen(false), 150)
    }

    const displayValue = selected ? selected.locale(momentLocale).format('L') : ''

    return (
        <>
            <TextField
                className={`${classes.margins}${className ? ` ${className}` : ''}`}
                variant="outlined"
                fullWidth
                size={size}
                disabled={disabled}
                id={name}
                label={label}
                value={displayValue}
                onClick={() => {
                    if (disabled || preventReopen) return
                    setOpen(true)
                }}
                onFocus={() => {
                    if (disabled || preventReopen) return
                    setOpen(true)
                }}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                edge="end"
                                style={{ padding: 6, color: isDarkMode ? '#cbd5e0' : undefined }}
                                tabIndex={-1}
                            >
                                <CalendarTodayOutlinedIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                inputProps={{ placeholder: displayValue ? undefined : moment.localeData(momentLocale).longDateFormat('L').toLowerCase(), readOnly: true }}
                InputLabelProps={{ shrink: true }}
            />

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth={false}
                classes={{ paper: classes.pickerDialog }}
                PaperProps={{ style: { backgroundColor: isDarkMode ? '#2d3748' : '#FFFFFF' } }}
            >
                <div className={classes.pickerRoot}>
                    <div className={classes.headerRow}>
                        <IconButton
                            className={classes.navBtn}
                            disabled={minMoment != null && displayMonth.clone().subtract(1, 'month').endOf('month').isBefore(minMoment, 'day')}
                            onClick={() => setDisplayMonth((m) => m.clone().subtract(1, 'month'))}
                        >
                            <span className={classes.navArrow}>‹</span>
                        </IconButton>

                        <Typography className={classes.monthTitle}>{monthLabel}</Typography>

                        <IconButton
                            className={classes.navBtn}
                            onClick={() => setDisplayMonth((m) => m.clone().add(1, 'month'))}
                        >
                            <span className={classes.navArrow}>›</span>
                        </IconButton>
                    </div>

                    <div className={classes.weekHeader}>
                        {weekDays.map((d, i) => (
                            <div key={i} className={classes.weekday}>{d}</div>
                        ))}
                    </div>

                    <div className={classes.grid}>
                        {days.map((day) => {
                            const inMonth = day.month() === displayMonth.month()
                            const isSelected = selected ? day.isSame(selected, 'day') : false
                            const isToday = day.isSame(moment(), 'day')
                            const isPastDay = minMoment != null && day.isBefore(minMoment, 'day')
                            return (
                                <button
                                    key={day.format('YYYY-MM-DD')}
                                    type="button"
                                    disabled={isPastDay}
                                    className={[
                                        classes.dayCell,
                                        !inMonth ? classes.dayMuted : '',
                                        isToday && !isSelected ? classes.dayToday : '',
                                        isSelected ? classes.daySelected : '',
                                        isPastDay ? classes.dayDisabled : '',
                                    ].join(' ')}
                                    onClick={() => {
                                        setSelected(day.clone())
                                        setDisplayMonth(day.clone().startOf('month'))
                                        onChange(day.format('YYYY-MM-DD'))
                                    }}
                                >
                                    {day.date()}
                                </button>
                            )
                        })}
                    </div>

                    <div className={classes.footerRow}>
                        <button type="button" className={classes.linkBtn} onClick={clearValue}>
                            {intl.formatMessage({ id: 'waoDateTime.clear', defaultMessage: 'Clear' })}
                        </button>
                        <button
                            type="button"
                            className={classes.linkBtn}
                            onClick={() => {
                                const today = moment()
                                setSelected(today.clone())
                                setDisplayMonth(today.clone().startOf('month'))
                                onChange(today.format('YYYY-MM-DD'))
                            }}
                        >
                            {intl.formatMessage({ id: 'waoDateTime.today', defaultMessage: 'Today' })}
                        </button>
                    </div>
                </div>

                <DialogActions className={classes.actions}>
                    <Button
                        onClick={() => {
                            setPreventReopen(true)
                            setOpen(false)
                            setTimeout(() => setPreventReopen(false), 150)
                        }}
                    >
                        {intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
                    </Button>
                    <Button color="primary" onClick={applyAndClose} disabled={minMoment != null && selected !== null && selected.isBefore(minMoment, 'day')}>
                        {intl.formatMessage({ id: 'ok', defaultMessage: 'OK' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default WaoDateOnly
